import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, FileText, CheckCircle2, Clock, Loader2, XCircle, 
  TrendingUp, BarChart2, ChevronLeft, ChevronRight, ListFilter,
  Download, RefreshCw
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import * as engineService from "@/shared/api/services/engineService";
import * as settlementService from "@/shared/api/services/settlementService";

const statusConfig = {
  completed:  { icon: CheckCircle2, color: "text-emerald-400", bg: "rgba(52,211,153,0.1)",  border: "rgba(52,211,153,0.25)",  label: "Completed" },
  pending:    { icon: Clock,        color: "text-amber-400",   bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.25)",  label: "Pending" },
  processing: { icon: Loader2,      color: "text-cyan-400",    bg: "rgba(34,211,238,0.1)",  border: "rgba(34,211,238,0.25)",  label: "Processing" },
  failed:     { icon: XCircle,      color: "text-red-400",     bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Failed" },
};

function StatCard({ title, value, icon: Icon, accent, gradient, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)}
      className="relative" style={{ perspective: 900 }}
    >
      <motion.div animate={{ opacity: hovered ? 1 : 0 }} transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1a, transparent 68%)`, filter: "blur(8px)" }}
      />
      <motion.div animate={{ y: hovered ? -6 : 0 }} transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
          border: `1px solid ${hovered ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 18px 50px rgba(0,0,0,0.5), 0 0 28px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />
        <motion.div animate={{ x: hovered ? "108%" : "-108%" }} transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100">{value}</motion.p>
          </div>
          <motion.div animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${gradient})`, boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`, transition: "box-shadow 0.3s" }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function EngineSettlements() {
  const { t } = useLanguage();
  const engineId = auth.getUserData()?.userId;

  // 1. Hierarchy Scoping (similar to EngineTransactions)
  const { data: rawResellers = [], isFetching: resellersFetching } = useQuery({
    queryKey: ["engine-hierarchy-resellers", engineId],
    queryFn: async () => {
      const res = await engineService.getMyResellers(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const { data: rawMerchants = [], isFetching: merchantsFetching } = useQuery({
    queryKey: ["engine-hierarchy-merchants", engineId],
    queryFn: async () => {
      const res = await engineService.getMyMerchants(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const filteredMerchants = useMemo(() => {
    if (!engineId) return [];
    const resellerIds = new Set(rawResellers.map(r => r.id));
    return rawMerchants.filter(m =>
      m.createdByEngineId === engineId || resellerIds.has(m.createdByResellerId)
    );
  }, [rawMerchants, rawResellers, engineId]);

  const allowedMerchantIds = useMemo(() => filteredMerchants.map(m => m.id), [filteredMerchants]);

  const { data: profile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  const resellerName = profile?.name || "Engine Partner";

  // 2. Filters & State
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [page, setPage] = useState(0);
  const PG = 10;
  
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectFocused, setSelectFocused] = useState(false);

  const targetMerchantIds = useMemo(() => {
    if (merchantFilter && merchantFilter !== "all") return [merchantFilter];
    return allowedMerchantIds.length > 0 ? allowedMerchantIds : ["__NO_SCOPE__"];
  }, [merchantFilter, allowedMerchantIds]);

  // 3. Main Query
  const { data: settlementData, isLoading, isFetching: settlementsFetching } = useQuery({
    queryKey: ["engine-settlements", page, PG, search, statusFilter, targetMerchantIds],
    queryFn: () => settlementService.getSettlements({
      page: page + 1,
      limit: PG,
      search,
      filters: {
        status: statusFilter !== "all" ? statusFilter.toUpperCase() : undefined,
      },
      merchantIds: targetMerchantIds,
    }),
    enabled: !!engineId && (merchantFilter !== "all" || allowedMerchantIds.length > 0),
    keepPreviousData: true,
  });

  const isSyncing = resellersFetching || merchantsFetching || settlementsFetching;
  const settlements = settlementData?.data || [];
  const totalItems = settlementData?.total || 0;
  const totalPages = settlementData?.totalPages || 0;

  // Stats calculation (from current view for now, as we don't have a separate stats query)
  const totalAmount = useMemo(() => 
    settlements.filter(s => s.status === "COMPLETED").reduce((sum, s) => sum + (s.net_amount || 0), 0)
  , [settlements]);
  
  const pendingCount = useMemo(() => 
    settlements.filter(s => s.status === "PENDING" || s.status === "PROCESSING").length
  , [settlements]);
  
  const completedCount = useMemo(() => 
    settlements.filter(s => s.status === "COMPLETED").length
  , [settlements]);

  const nums = useMemo(() => { 
    const a = []; 
    for (let i = Math.max(0, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) a.push(i); 
    return a; 
  }, [page, totalPages]);

  return (
    <EngineLayout currentPage="EngineSettlements" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full pb-10">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))", border: "1px solid rgba(34,211,238,0.15)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.45), rgba(139,92,246,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(34,211,238,0.07), transparent 70%)" }} />
          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-400 mb-1">Accounting</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("settlementTitle") || "Settlements"}</h1>
              <p className="text-sm text-slate-400">
                {t("totalFound") || "Total Found"}: <span className="text-cyan-400 font-semibold">{totalItems}</span>
              </p>
            </div>
            {isSyncing && (
              <div className="flex items-center gap-2 text-xs text-cyan-400/70 bg-cyan-400/5 px-3 py-1.5 rounded-full border border-cyan-400/10">
                <Loader2 className="h-3 w-3 animate-spin" />
                Updating Data...
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title={t("totalSettled") || "Total Settled"}     value={`฿${totalAmount.toLocaleString()}`} icon={TrendingUp}   gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0} />
          <StatCard title={t("completed") || "Completed"}            value={completedCount}                      icon={CheckCircle2} gradient="#34d399, #059669"  accent="#34d399" delay={0.05} />
          <StatCard title={t("pending") || "Pending"}                value={pendingCount}                        icon={Clock}        gradient="#fbbf24, #d97706"  accent="#fbbf24" delay={0.1} />
        </div>

        {/* Filters */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="flex flex-wrap gap-3 items-center"
        >
          <div className="flex items-center gap-2 text-slate-500 mr-1">
            <ListFilter className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Filters</span>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder={t("searchPlaceholder") || "Search ID or Merchant…"}
              className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
              style={{
                background: "rgba(2,6,23,0.7)",
                border: `1px solid ${searchFocused ? "rgba(34,211,238,0.3)" : "rgba(148,163,184,0.12)"}`,
                boxShadow: searchFocused ? "0 0 0 3px rgba(34,211,238,0.07)" : "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
            />
          </div>

          {/* Merchant Filter */}
          <div className="h-10 flex items-center px-3 rounded-xl" style={{
            background: "rgba(2,6,23,0.7)", minWidth: 160,
            border: `1px solid ${selectFocused ? "rgba(34,211,238,0.3)" : "rgba(148,163,184,0.12)"}`,
            transition: "border-color 0.2s",
          }}>
            <select value={merchantFilter} onChange={e => { setMerchantFilter(e.target.value); setPage(0); }}
              className="outline-none text-slate-300 text-sm w-full cursor-pointer" style={{ background: "transparent" }}
            >
              <option value="all" style={{ background: "#020617" }}>{t("allMerchants") || "All Merchants"}</option>
              {filteredMerchants.map(m => (
                <option key={m.id} value={m.id} style={{ background: "#020617" }}>{m.name}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="h-10 flex items-center px-3 rounded-xl" style={{
            background: "rgba(2,6,23,0.7)", minWidth: 140,
            border: `1px solid ${selectFocused ? "rgba(34,211,238,0.3)" : "rgba(148,163,184,0.12)"}`,
            transition: "border-color 0.2s",
          }}>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              className="outline-none text-slate-300 text-sm w-full cursor-pointer" style={{ background: "transparent" }}
            >
              <option value="all" style={{ background: "#020617" }}>{t("allStatus") || "All Status"}</option>
              <option value="completed" style={{ background: "#020617" }}>{t("completed") || "Completed"}</option>
              <option value="pending" style={{ background: "#020617" }}>{t("pending") || "Pending"}</option>
              <option value="processing" style={{ background: "#020617" }}>{t("processing") || "Processing"}</option>
              <option value="failed" style={{ background: "#020617" }}>Failed</option>
            </select>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="relative rounded-2xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))", border: "1px solid rgba(148,163,184,0.09)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent)" }} />
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "rgba(2,6,23,0.4)" }}>
                  {[t("settlement")||"Settlement", t("merchant")||"Merchant", t("deposits")||"Deposits", t("withdrawals")||"Withdrawals", t("netAmount")||"Net Amount", t("status")||"Status", t("period")||"Period"].map((h, i) => (
                    <th key={i} className={`px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 ${(i >= 2 && i <= 4) ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={isSyncing ? "opacity-60 transition-opacity" : ""}>
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <tr><td colSpan={7} className="text-center py-14">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                        <span className="text-sm">{t("loading")||"Loading…"}</span>
                      </div>
                    </td></tr>
                  ) : settlements.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-14">
                      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.1)" }}>
                          <FileText className="h-6 w-6 text-slate-600" />
                        </div>
                        <p className="text-slate-600 text-sm">{t("noSettlements")||"No settlements found"}</p>
                      </motion.div>
                    </td></tr>
                  ) : settlements.map((s, i) => {
                    const st = statusConfig[s.status.toLowerCase()] || statusConfig.pending;
                    const StatusIcon = st.icon;
                    return (
                      <motion.tr key={s.id}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        transition={{ delay: i * 0.03 }}
                        style={{ borderBottom: "1px solid rgba(148,163,184,0.05)", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <motion.div whileHover={{ scale: 1.1, rotate: 5 }}
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(2,132,199,0.1))", border: "1px solid rgba(34,211,238,0.2)", transition: "box-shadow 0.25s" }}
                            >
                              <FileText className="h-4 w-4 text-cyan-400" />
                            </motion.div>
                            <span className="font-mono text-xs text-slate-200">{s.id.slice(0, 8)}...</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col">
                            <span className="text-sm text-slate-200 font-medium">{s.merchant?.name || s.merchant_code}</span>
                            <span className="text-[10px] text-slate-500 font-mono uppercase">{s.merchant?.code}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-right font-mono text-xs text-slate-400">
                          ฿{s.total_deposits?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3.5 text-right font-mono text-xs text-slate-400">
                          ฿{s.total_withdrawals?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <span className="font-bold text-cyan-400">฿{s.net_amount?.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider" style={{ background: st.bg, border: `1px solid ${st.border}` }}>
                            <StatusIcon className={`h-3 w-3 ${st.color} ${s.status === "PROCESSING" ? "animate-spin" : ""}`} />
                            <span className={st.color}>{st.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-slate-500 font-mono">
                          {s.period_start && s.period_end
                            ? `${format(new Date(s.period_start), "MMM d")} - ${format(new Date(s.period_end), "MMM d")}`
                            : t("nA") || "N/A"}
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex items-center justify-between bg-slate-900/40 p-4 rounded-2xl border border-slate-800/50"
          >
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="text-slate-200">{page * PG + 1}</span> to <span className="text-slate-200">{Math.min((page + 1) * PG, totalItems)}</span> of <span className="text-slate-200">{totalItems}</span> settlements
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setPage(p => p - 1)} disabled={page === 0}
                className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700/50 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {nums.map(n => (
                <button 
                  key={n} onClick={() => setPage(n)}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold transition-all ${n === page ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/20" : "bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:bg-slate-700/50"}`}
                >
                  {n + 1}
                </button>
              ))}

              <button 
                onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700/50 transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </EngineLayout>
  );
}